import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from '../../../components/modal/modal.component';
import { GreenTick } from '../../../assets';
import {
  ActionItemTaskPayload,
  ApiError,
  ShareUser,
  StructuredTask,
  TaskPriority,
  apiService,
} from '../../../services/api.service';
import { useTasks } from '../../../contexts/tasks.context';
import classes from './create-tasks-modal.module.scss';

const PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];

type Row = {
  include: boolean;
  title: string;
  /** YYYY-MM-DD или '' */
  dueDate: string;
  priority: TaskPriority;
  /** id пользователя-исполнителя ('' — не назначен). */
  assigneeId: string;
  /** Имя ответственного из протокола — для отображения и описания задачи. */
  assigneeName: string;
};

/** «до 15.07.2026» → «2026-07-15»; без даты в тексте — пустая строка. */
export function parseDeadlineToIso(deadline?: string | null): string {
  const m = (deadline || '').match(/(\d{1,2})[./](\d{1,2})[./](\d{4})/);
  if (!m) return '';
  const [, d, mo, y] = m;
  const day = Number(d);
  const month = Number(mo);
  if (day < 1 || day > 31 || month < 1 || month > 12) return '';
  return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Русские приоритеты протокола → значения бэкенда; дефолт medium. */
export function mapPriority(priority?: string | null): TaskPriority {
  const p = (priority || '').toLowerCase();
  if (p.includes('сроч') || p.includes('критич') || p.includes('urgent')) return 'urgent';
  if (p.includes('высок') || p.includes('high')) return 'high';
  if (p.includes('низк') || p.includes('low')) return 'low';
  return 'medium';
}

function normalizeName(value: string): string {
  return value.toLowerCase().replace(/[^\wа-яё]+/gi, ' ').trim();
}

/** Пытается сопоставить ответственного из протокола с пользователем портала. */
function matchUser(assignee: string, users: ShareUser[]): number | '' {
  const target = normalizeName(assignee);
  if (!target || target === '—' || target === '-') return '';
  for (const user of users) {
    const full = normalizeName(user.full_name || '');
    if (full && (full.includes(target) || target.includes(full))) return user.id;
  }
  return '';
}

type Props = {
  open: boolean;
  onClose: () => void;
  meetingId: string;
  sourceTasks: StructuredTask[];
};

const CreateTasksModal = ({ open, onClose, meetingId, sourceTasks }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refresh } = useTasks();

  const [rows, setRows] = useState<Row[]>([]);
  const [users, setUsers] = useState<ShareUser[] | null>(null);
  const [existingCount, setExistingCount] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    // Исполнитель: GET /api/admin/users доступен только админу (403 — штатно, null).
    apiService.getUsersForSharing()
      .then(list => {
        if (cancelled) return;
        setUsers(list);
        if (list) {
          setRows(prev => prev.map(row => (
            row.assigneeId === '' && row.assigneeName
              ? { ...row, assigneeId: String(matchUser(row.assigneeName, list) || '') }
              : row
          )));
        }
      })
      .catch(() => {
        if (!cancelled) setUsers(null);
      });

    // Защита от дублей: сколько задач уже создано по этой встрече.
    apiService.getTasks({ meeting_id: meetingId, limit: 1 })
      .then(result => {
        if (!cancelled) setExistingCount(result.total);
      })
      .catch(() => {
        if (!cancelled) setExistingCount(0);
      });

    setRows(sourceTasks.map(task => ({
      include: true,
      title: task.task,
      dueDate: parseDeadlineToIso(task.deadline),
      priority: mapPriority(task.priority),
      assigneeId: '',
      assigneeName: task.assignee && task.assignee !== '—' ? task.assignee : '',
    })));

    return () => { cancelled = true; };
  }, [open, meetingId, sourceTasks]);

  const updateRow = (index: number, patch: Partial<Row>) => {
    setRows(prev => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const selectedRows = useMemo(
    () => rows.filter(row => row.include && row.title.trim()),
    [rows],
  );

  const handleSubmit = async () => {
    if (!selectedRows.length || submitting) return;
    setSubmitting(true);
    try {
      const items: ActionItemTaskPayload[] = selectedRows.map(row => ({
        title: row.title.trim(),
        // Ответственный не сопоставлен с пользователем — сохраняем имя в описании,
        // чтобы информация из протокола не потерялась.
        description: !row.assigneeId && row.assigneeName
          ? `${t('meeting-tasks.assignee-from-protocol')}: ${row.assigneeName}`
          : undefined,
        assignee_id: row.assigneeId ? Number(row.assigneeId) : undefined,
        due_date: row.dueDate || undefined,
        priority: row.priority,
      }));
      const created = await apiService.createTasksFromActionItems(meetingId, items);
      void refresh();
      toast(
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <GreenTick />
          <span>
            {t('meeting-tasks.success', { count: created.length })}{' '}
            <span
              role="link"
              style={{ color: 'var(--color-primary)', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => navigate('/tasks')}
            >
              {t('meeting-tasks.go-to-tasks')}
            </span>
          </span>
        </div>,
      );
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        toast.error(t('meeting-tasks.forbidden'));
      } else {
        toast.error(e instanceof Error ? e.message : t('meeting-tasks.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={open}
      handleClose={onClose}
      style={{ padding: 0, borderRadius: '1.5rem' }}
    >
      <div className={classes['modal']}>
        <div className={classes['modal-header']}>
          {t('meeting-tasks.modal-heading')}
        </div>

        <div className={classes['modal-body']}>
          <p className={classes['hint']}>{t('meeting-tasks.modal-hint')}</p>

          {existingCount > 0 && (
            <p className={classes['existing-note']}>
              {t('meeting-tasks.existing-note', { count: existingCount })}
            </p>
          )}

          <table className={classes['table']}>
            <thead>
              <tr>
                <th />
                <th>{t('meeting-tasks.col-title')}</th>
                <th>{t('meeting-tasks.col-assignee')}</th>
                <th>{t('meeting-tasks.col-due')}</th>
                <th>{t('meeting-tasks.col-priority')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className={row.include ? undefined : classes['row-excluded']}>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.include}
                      onChange={(e) => updateRow(i, { include: e.target.checked })}
                    />
                  </td>
                  <td style={{ minWidth: '16rem' }}>
                    <input
                      type="text"
                      value={row.title}
                      disabled={!row.include}
                      onChange={(e) => updateRow(i, { title: e.target.value })}
                    />
                  </td>
                  <td style={{ minWidth: '10rem' }}>
                    {users ? (
                      <select
                        value={row.assigneeId}
                        disabled={!row.include}
                        onChange={(e) => updateRow(i, { assigneeId: e.target.value })}
                      >
                        <option value="">{t('meeting-tasks.assignee-none')}</option>
                        {users.map(user => (
                          <option key={user.id} value={String(user.id)}>
                            {user.full_name || user.email}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={classes['assignee-plain']}
                        title={t('meeting-tasks.assignee-later') || ''}
                      >
                        {row.assigneeName || t('meeting-tasks.assignee-later')}
                      </span>
                    )}
                  </td>
                  <td>
                    <input
                      type="date"
                      value={row.dueDate}
                      disabled={!row.include}
                      onChange={(e) => updateRow(i, { dueDate: e.target.value })}
                    />
                  </td>
                  <td>
                    <select
                      value={row.priority}
                      disabled={!row.include}
                      onChange={(e) => updateRow(i, { priority: e.target.value as TaskPriority })}
                    >
                      {PRIORITIES.map(p => (
                        <option key={p} value={p}>{t(`tasks-board.priority-${p}`)}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={classes['modal-footer']}>
          <button type="button" onClick={onClose} disabled={submitting}>
            {t('action.cancel')}
          </button>
          <button
            type="button"
            onClick={() => { void handleSubmit() }}
            disabled={!selectedRows.length || submitting}
          >
            {t('meeting-tasks.submit', { count: selectedRows.length })}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateTasksModal;
